import React from 'react';
import { AIInlineHint } from '../ai/AIInlineHint';

export interface KRCardNotes {
  accomplishments: string;
  challenges: string;
  nextSteps: string;
}

// The backend inline-hint endpoint only supports these two fields. Maps the
// card's note keys to the hint field; `challenges` intentionally has no hint.
const HINT_FIELD: Partial<Record<keyof KRCardNotes, 'accomplishments' | 'next_steps'>> = {
  accomplishments: 'accomplishments',
  nextSteps: 'next_steps',
};

interface KeyResultLike {
  id: string;
  title: string;
  currentValue: number;
  startValue: number;
  targetValue: number;
}

interface CheckInKRCardProps {
  okrId: string;
  kr: KeyResultLike;
  value: number;
  notes: KRCardNotes;
  expanded: boolean;
  onToggle: () => void;
  onValueChange: (value: number) => void;
  onNoteChange: (field: keyof KRCardNotes, value: string) => void;
  staleLabel?: string; // e.g. "3 weeks" — when the KR has had no notes recently
}

const isEmpty = (n: KRCardNotes) =>
  !n.accomplishments.trim() && !n.challenges.trim() && !n.nextSteps.trim();

const filledCount = (n: KRCardNotes) =>
  [n.accomplishments, n.challenges, n.nextSteps].filter((v) => v.trim()).length;

export const CheckInKRCard: React.FC<CheckInKRCardProps> = ({
  okrId,
  kr,
  value,
  notes,
  expanded,
  onToggle,
  onValueChange,
  onNoteChange,
  staleLabel,
}) => {
  const min = Math.min(kr.startValue, kr.targetValue);
  const max = Math.max(kr.startValue, kr.targetValue);
  const empty = isEmpty(notes);
  const filled = filledCount(notes);

  return (
    <div className="border border-gray-200 rounded-xl mb-3 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={`w-full flex items-center justify-between px-4 py-3 text-left ${
          expanded ? 'bg-white' : 'bg-gray-50'
        }`}
      >
        <span className="text-sm font-semibold text-gray-900">{kr.title}</span>
        <span className="flex items-center gap-2 text-xs text-gray-400">
          {empty ? (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">
              no notes{staleLabel ? ` · ${staleLabel}` : ''}
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">
              {filled} {filled === 1 ? 'note' : 'notes'}
            </span>
          )}
          <span className="text-gray-400">{Math.round(value)}%</span>
          <span className="text-primary-600 font-bold">{expanded ? '▾' : '▸'}</span>
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>{min}%</span>
            <span className="font-semibold text-gray-700">Target {kr.targetValue}%</span>
            <span>{max}%</span>
          </div>
          <input
            type="range"
            min={min}
            max={max}
            step={(max - min) / 100 || 1}
            value={value}
            onChange={(e) => onValueChange(Number(e.target.value))}
            className="w-full accent-primary-600"
          />
          <p className="text-center text-xs text-primary-600 font-semibold mb-3">
            Current: {value.toFixed(1)}%
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(['accomplishments', 'challenges', 'nextSteps'] as const).map((field) => {
              const labels: Record<typeof field, string> = {
                accomplishments: 'Accomplishments',
                challenges: 'Challenges',
                nextSteps: 'Next Steps',
              };
              const placeholders: Record<typeof field, string> = {
                accomplishments: 'What did you accomplish for this KR?',
                challenges: 'What challenges on this KR?',
                nextSteps: 'Next steps for this KR?',
              };
              const fieldValue = notes[field];
              return (
                <div key={field}>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                    {labels[field]}
                  </label>
                  <textarea
                    value={fieldValue}
                    onChange={(e) => onNoteChange(field, e.target.value)}
                    placeholder={placeholders[field]}
                    rows={3}
                    className={`w-full px-3 py-2 text-sm border rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      fieldValue.trim() ? 'border-gray-300' : 'border-dashed border-amber-300 bg-amber-50'
                    }`}
                  />
                  {HINT_FIELD[field] && (
                    <AIInlineHint okrId={okrId} field={HINT_FIELD[field]!} text={fieldValue} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
